import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { ratioAndDuaEnabledFor } from '@gonfalon/metrics/ratioAndDuaEnabledFor';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useQuery } from '@tanstack/react-query';

import type { MetricForm } from './formUtils';

// Data sources already saved on the metric, so an existing ratio metric on a warehouse outside the
// rollout stays editable. Only terms pointed at a new data source are checked.
export type PersistedRatioSources = {
  numeratorDataSourceKey?: string;
  denominatorDataSourceKey?: string;
};

export function useIsRatioBlockedForUnsupportedWarehouse({
  control,
  isRatioMode,
  persisted,
}: {
  control: Control<MetricForm>;
  isRatioMode: boolean;
  persisted?: PersistedRatioSources;
}) {
  const projectKey = useProjectKey();
  const { data } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const numeratorDataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const denominator = useWatch({ control, name: 'denominator' });

  if (!isRatioMode) {
    return false;
  }

  const items = data?.items ?? [];
  const integrationKeyFor = (dataSourceKey?: string) => items.find((ds) => ds.key === dataSourceKey)?.integrationKey;
  const termBlocked = (dataSourceKey?: string, persistedDataSourceKey?: string) => {
    if (persistedDataSourceKey !== undefined && persistedDataSourceKey === dataSourceKey) {
      return false;
    }
    return !ratioAndDuaEnabledFor(integrationKeyFor(dataSourceKey));
  };

  return (
    termBlocked(numeratorDataSourceKey, persisted?.numeratorDataSourceKey) ||
    termBlocked(denominator?.dataSourceKey, persisted?.denominatorDataSourceKey)
  );
}
